import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  Gateway,
  GenericStringValueProperty,
} from '@vi-iot/client';

import { AppState } from '../app.state';
import { GatewaySelectors, GatewayDeviceGroup } from '../gateway';
import { RemoteDeviceMappers } from './remote-device.mappers';
import { ZigbeeTopologyDeviceData } from './remote-device.model';

import { RemoteDeviceState } from './remote-device.state';
import { ConfigurationSelectors } from '../configuration';
import { InstallationDetailsSelectors } from '../installation-details';
import { InstallationFeaturesSelectors } from '../installation-features';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';

export namespace RemoteDeviceSelectors {
  export const remoteDevice = (state: AppState): RemoteDeviceState => state.remoteDevice;
  export const installationFeatures = (state: AppState): InstallationFeaturesState =>
    state.installationFeatures;

  export function isZigbeeTopologyAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      GatewaySelectors.getAllZigbeeTypeDevicesIds(),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        zigbeeDevicesIds: string[],
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ): boolean => {
        const canDisplay = zigbeeDevicesIds.some((deviceId) => {
          const deviceParentFeature: Feature<
            GenericStringValueProperty,
            {}
          > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.deviceZigbeeParent,
            deviceId,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );
          const parentId = deviceParentFeature?.properties?.value?.value;
          const isParentIdPresent = !!parentId;

          return isParentIdPresent;
        });

        return canDisplay;
      }
    );
  }

  export function getZigbeeTopologyDeviceData(): MemoizedSelector<
    AppState,
    ZigbeeTopologyDeviceData,
    DefaultProjectorFn<ZigbeeTopologyDeviceData>
  > {
    return createSelector(
      GatewaySelectors.getAllZigbeeTypeDevicesIds(),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      GatewaySelectors.getDevices(),
      installationFeatures,
      (
        zigbeeDevicesIds: string[],
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        devices: GatewayDeviceGroup[],
        installationFeaturesState: InstallationFeaturesState
      ): ZigbeeTopologyDeviceData => {
        return RemoteDeviceMappers.createGraphRelationsFromZigbeeDevices(
          zigbeeDevicesIds,
          devices,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial,
          installationFeaturesState
        );
      }
    );
  }

  export function getDevicesNames(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      GatewaySelectors.getAllZigbeeTypeDevicesIds(),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        zigbeeDevicesIds: string[],
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const result = zigbeeDevicesIds.map((deviceId) => {
          const feature: Feature<
            DeviceNameProperties,
            DeviceNameCommands
          > = InstallationFeaturesSelectors.getInstallationDeviceFeature(
            FeatureNames.deviceName,
            deviceId,
            featuresBaseUri,
            stateInstallationId,
            stateGatewaySerial,
            installationFeaturesState
          );
          const deviceName = feature?.properties?.name?.value;

          return deviceName;
        });

        return result;
      }
    );
  }
}
